// ---------------------------------------------------------------------------
// Client-side formula evaluator for derived_local DAG columns.
// Tokenizer → recursive-descent parser → tree-walking evaluator.
// ---------------------------------------------------------------------------

// ---- AST ------------------------------------------------------------------

type ASTNode =
  | { type: 'number'; value: number }
  | { type: 'string'; value: string }
  | { type: 'field'; name: string }
  | { type: 'unary'; op: '-'; operand: ASTNode }
  | { type: 'binary'; op: string; left: ASTNode; right: ASTNode }
  | { type: 'call'; name: string; args: ASTNode[] }

// ---- Tokens ---------------------------------------------------------------

type TokenType =
  | 'NUMBER'
  | 'STRING'
  | 'IDENT'
  | 'PLUS'
  | 'MINUS'
  | 'STAR'
  | 'SLASH'
  | 'LPAREN'
  | 'RPAREN'
  | 'COMMA'
  | 'EQ'
  | 'NEQ'
  | 'GT'
  | 'GTE'
  | 'LT'
  | 'LTE'
  | 'EOF'

interface Token {
  type: TokenType
  value: string
}

// ---- Tokenizer ------------------------------------------------------------

function tokenize(expr: string): Token[] {
  const tokens: Token[] = []
  let i = 0

  while (i < expr.length) {
    const ch = expr[i]

    // Whitespace
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i++
      continue
    }

    // Quoted string (double or single quotes)
    if (ch === '"' || ch === "'") {
      const quote = ch
      let str = ''
      i++ // skip opening quote
      while (i < expr.length && expr[i] !== quote) {
        if (expr[i] === '\\' && i + 1 < expr.length) {
          i++
          str += expr[i]
        } else {
          str += expr[i]
        }
        i++
      }
      i++ // skip closing quote
      tokens.push({ type: 'STRING', value: str })
      continue
    }

    // Number
    if (
      (ch >= '0' && ch <= '9') ||
      (ch === '.' &&
        i + 1 < expr.length &&
        expr[i + 1] >= '0' &&
        expr[i + 1] <= '9')
    ) {
      let num = ''
      while (
        i < expr.length &&
        ((expr[i] >= '0' && expr[i] <= '9') || expr[i] === '.')
      ) {
        num += expr[i]
        i++
      }
      tokens.push({ type: 'NUMBER', value: num })
      continue
    }

    // Identifier (field names, function names — letters, digits, underscores, colons)
    if ((ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z') || ch === '_') {
      let ident = ''
      while (
        i < expr.length &&
        ((expr[i] >= 'a' && expr[i] <= 'z') ||
          (expr[i] >= 'A' && expr[i] <= 'Z') ||
          (expr[i] >= '0' && expr[i] <= '9') ||
          expr[i] === '_' ||
          expr[i] === ':')
      ) {
        ident += expr[i]
        i++
      }
      tokens.push({ type: 'IDENT', value: ident })
      continue
    }

    // Two-char operators
    if (ch === '=' && expr[i + 1] === '=') {
      tokens.push({ type: 'EQ', value: '==' })
      i += 2
      continue
    }
    if (ch === '!' && expr[i + 1] === '=') {
      tokens.push({ type: 'NEQ', value: '!=' })
      i += 2
      continue
    }
    if (ch === '>' && expr[i + 1] === '=') {
      tokens.push({ type: 'GTE', value: '>=' })
      i += 2
      continue
    }
    if (ch === '<' && expr[i + 1] === '=') {
      tokens.push({ type: 'LTE', value: '<=' })
      i += 2
      continue
    }

    // Single-char operators
    const singleCharMap: Record<string, TokenType> = {
      '+': 'PLUS',
      '-': 'MINUS',
      '*': 'STAR',
      '/': 'SLASH',
      '(': 'LPAREN',
      ')': 'RPAREN',
      ',': 'COMMA',
      '>': 'GT',
      '<': 'LT',
    }
    const mapped = singleCharMap[ch]
    if (mapped) {
      tokens.push({ type: mapped, value: ch })
      i++
      continue
    }

    throw new FormulaError(`Unexpected character: '${ch}' at position ${i}`)
  }

  tokens.push({ type: 'EOF', value: '' })
  return tokens
}

// ---- Parser ---------------------------------------------------------------

class FormulaError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'FormulaError'
  }
}

class Parser {
  private tokens: Token[]
  private pos = 0

  constructor(tokens: Token[]) {
    this.tokens = tokens
  }

  private peek(): Token {
    return this.tokens[this.pos]
  }

  private advance(): Token {
    const t = this.tokens[this.pos]
    this.pos++
    return t
  }

  private expect(type: TokenType): Token {
    const t = this.peek()
    if (t.type !== type) {
      throw new FormulaError(`Expected ${type}, got ${t.type} (${t.value})`)
    }
    return this.advance()
  }

  parse(): ASTNode {
    const node = this.comparison()
    if (this.peek().type !== 'EOF') {
      throw new FormulaError(`Unexpected token: ${this.peek().value}`)
    }
    return node
  }

  // Precedence 1: == != > >= < <=
  private comparison(): ASTNode {
    let left = this.addition()
    while (
      this.peek().type === 'EQ' ||
      this.peek().type === 'NEQ' ||
      this.peek().type === 'GT' ||
      this.peek().type === 'GTE' ||
      this.peek().type === 'LT' ||
      this.peek().type === 'LTE'
    ) {
      const op = this.advance().value
      const right = this.addition()
      left = { type: 'binary', op, left, right }
    }
    return left
  }

  // Precedence 2: + -
  private addition(): ASTNode {
    let left = this.multiplication()
    while (this.peek().type === 'PLUS' || this.peek().type === 'MINUS') {
      const op = this.advance().value
      const right = this.multiplication()
      left = { type: 'binary', op, left, right }
    }
    return left
  }

  // Precedence 3: * /
  private multiplication(): ASTNode {
    let left = this.unary()
    while (this.peek().type === 'STAR' || this.peek().type === 'SLASH') {
      const op = this.advance().value
      const right = this.unary()
      left = { type: 'binary', op, left, right }
    }
    return left
  }

  // Precedence 4: unary -
  private unary(): ASTNode {
    if (this.peek().type === 'MINUS') {
      this.advance()
      const operand = this.unary()
      return { type: 'unary', op: '-', operand }
    }
    return this.primary()
  }

  // Precedence 5: literals, field refs, function calls, grouped exprs
  private primary(): ASTNode {
    const t = this.peek()

    if (t.type === 'NUMBER') {
      this.advance()
      return { type: 'number', value: parseFloat(t.value) }
    }

    if (t.type === 'STRING') {
      this.advance()
      return { type: 'string', value: t.value }
    }

    if (t.type === 'IDENT') {
      this.advance()
      // Function call: IDENT(...)
      if (this.peek().type === 'LPAREN') {
        this.advance() // consume '('
        const args: ASTNode[] = []
        if (this.peek().type !== 'RPAREN') {
          args.push(this.comparison())
          while (this.peek().type === 'COMMA') {
            this.advance()
            args.push(this.comparison())
          }
        }
        this.expect('RPAREN')
        return { type: 'call', name: t.value.toUpperCase(), args }
      }
      // Field reference
      return { type: 'field', name: t.value }
    }

    if (t.type === 'LPAREN') {
      this.advance()
      const node = this.comparison()
      this.expect('RPAREN')
      return node
    }

    throw new FormulaError(`Unexpected token: ${t.type} (${t.value})`)
  }
}

// ---- Evaluator ------------------------------------------------------------

type Value = number | string | boolean | null

function toNumber(v: Value): number | null {
  if (v === null || v === undefined) return null
  if (typeof v === 'number') return v
  if (typeof v === 'boolean') return v ? 1 : 0
  const n = parseFloat(String(v))
  return isNaN(n) ? null : n
}

function isTruthy(v: Value): boolean {
  if (v === null || v === undefined || v === false || v === 0 || v === '')
    return false
  return true
}

function evaluate(node: ASTNode, cells: Record<string, unknown>): Value {
  switch (node.type) {
    case 'number':
      return node.value
    case 'string':
      return node.value
    case 'field': {
      const v = cells[node.name]
      if (v === undefined || v === null) return null
      if (
        typeof v === 'number' ||
        typeof v === 'string' ||
        typeof v === 'boolean'
      )
        return v
      return String(v)
    }
    case 'unary': {
      const operand = evaluate(node.operand, cells)
      const n = toNumber(operand)
      return n === null ? null : -n
    }
    case 'binary':
      return evaluateBinary(node.op, node.left, node.right, cells)
    case 'call':
      return evaluateCall(node.name, node.args, cells)
  }
}

function evaluateBinary(
  op: string,
  leftNode: ASTNode,
  rightNode: ASTNode,
  cells: Record<string, unknown>
): Value {
  const left = evaluate(leftNode, cells)
  const right = evaluate(rightNode, cells)

  // Normalize null to '' so unset fields compare equal to empty string
  const normL = left === null ? '' : left
  const normR = right === null ? '' : right

  // Comparison operators work on raw values (null-safe)
  switch (op) {
    case '==':
      return normL === normR
    case '!=':
      return normL !== normR
    case '>': {
      const l = toNumber(left)
      const r = toNumber(right)
      return l !== null && r !== null ? l > r : null
    }
    case '>=': {
      const l = toNumber(left)
      const r = toNumber(right)
      return l !== null && r !== null ? l >= r : null
    }
    case '<': {
      const l = toNumber(left)
      const r = toNumber(right)
      return l !== null && r !== null ? l < r : null
    }
    case '<=': {
      const l = toNumber(left)
      const r = toNumber(right)
      return l !== null && r !== null ? l <= r : null
    }
  }

  // Arithmetic: null propagation
  if (op === '+' && typeof left === 'string' && typeof right === 'string') {
    return left + right
  }

  const ln = toNumber(left)
  const rn = toNumber(right)
  if (ln === null || rn === null) return null

  switch (op) {
    case '+':
      return ln + rn
    case '-':
      return ln - rn
    case '*':
      return ln * rn
    case '/':
      return rn === 0 ? null : ln / rn
    default:
      return null
  }
}

function evaluateCall(
  name: string,
  argNodes: ASTNode[],
  cells: Record<string, unknown>
): Value {
  switch (name) {
    case 'IF': {
      const cond = evaluate(argNodes[0], cells)
      return isTruthy(cond)
        ? evaluate(argNodes[1], cells)
        : argNodes[2]
          ? evaluate(argNodes[2], cells)
          : null
    }
    case 'AND': {
      for (const a of argNodes) {
        if (!isTruthy(evaluate(a, cells))) return false
      }
      return true
    }
    case 'OR': {
      for (const a of argNodes) {
        if (isTruthy(evaluate(a, cells))) return true
      }
      return false
    }
    case 'IFS': {
      // IFS(c1, v1, c2, v2, ..., else_c, else_v)
      for (let i = 0; i + 1 < argNodes.length; i += 2) {
        if (isTruthy(evaluate(argNodes[i], cells))) {
          return evaluate(argNodes[i + 1], cells)
        }
      }
      return null
    }
    case 'ROUND': {
      const num = toNumber(evaluate(argNodes[0], cells))
      const dec = toNumber(evaluate(argNodes[1], cells))
      if (num === null || dec === null) return null
      const factor = Math.pow(10, dec)
      return Math.round(num * factor) / factor
    }
    case 'COALESCE': {
      for (const a of argNodes) {
        const v = evaluate(a, cells)
        if (v !== null && v !== undefined) return v
      }
      return null
    }
    case 'CEILING': {
      const num = toNumber(evaluate(argNodes[0], cells))
      const sig = toNumber(evaluate(argNodes[1], cells))
      if (num === null || sig === null || sig === 0) return null
      return Math.ceil(num / sig) * sig
    }
    case 'FLOOR': {
      const num = toNumber(evaluate(argNodes[0], cells))
      const sig = toNumber(evaluate(argNodes[1], cells))
      if (num === null || sig === null || sig === 0) return null
      return Math.floor(num / sig) * sig
    }
    case 'MAX': {
      let max: number | null = null
      for (const a of argNodes) {
        const v = toNumber(evaluate(a, cells))
        if (v !== null && (max === null || v > max)) max = v
      }
      return max
    }
    case 'SUMIFS': {
      // Cross-row aggregation — deferred to server
      return null
    }
    default:
      return null
  }
}

// ---- Parse cache ----------------------------------------------------------

const parseCache = new Map<string, ASTNode | FormulaError>()
const warnedExprs = new Set<string>()

function parseExpr(expr: string): ASTNode {
  const cached = parseCache.get(expr)
  if (cached) {
    if (cached instanceof FormulaError) throw cached
    return cached
  }

  try {
    const tokens = tokenize(expr)
    const ast = new Parser(tokens).parse()
    parseCache.set(expr, ast)
    return ast
  } catch (err) {
    const error =
      err instanceof FormulaError ? err : new FormulaError(String(err))
    parseCache.set(expr, error)
    throw error
  }
}

// ---- Public API -----------------------------------------------------------

export function evaluateFormula(
  expr: string,
  cells: Record<string, unknown>
): number | string | boolean | null {
  try {
    const ast = parseExpr(expr)
    return evaluate(ast, cells)
  } catch (err) {
    if (!warnedExprs.has(expr)) {
      warnedExprs.add(expr)
      console.warn(`[formulaEvaluator] Error in expression "${expr}":`, err)
    }
    return null
  }
}
